import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, GraduationCap, BookOpen } from "lucide-react";

export default function LMSPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [active, setActive] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [showLesson, setShowLesson] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/lms/courses"); setCourses(data.courses || []); }
    finally { setLoading(false); }
  };

  const openCourse = async (c) => {
    setActive(c);
    try { const { data } = await api.get(`/lms/courses/${c.id}/lessons`); setLessons(data.lessons || []); } catch {}
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="lms-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ lms · courses</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">Your LMS</h1>
        </div>
        <button onClick={() => setShow(true)} data-testid="add-course-btn" className="inline-flex items-center gap-2 bg-[#FFD600] text-[#0A0C10] font-bold px-5 py-2.5 btn-brutal-shadow">
          <Plus size={16} /> New course
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
        {loading ? (
          <div className="bg-white p-6 text-sm text-neutral-500 col-span-full">Loading…</div>
        ) : courses.length === 0 ? (
          <div className="bg-white p-6 text-sm text-neutral-500 col-span-full text-center">No courses yet.</div>
        ) : courses.map((c) => (
          <button key={c.id} onClick={() => openCourse(c)} className="bg-white p-5 text-left hover:bg-neutral-50">
            <GraduationCap size={22} className="text-[#002FA7]" />
            <div className="mt-3 font-display font-semibold">{c.title}</div>
            <div className="text-xs text-neutral-500 mt-1">{c.subject} · {c.grade}</div>
            <div className="text-xs text-neutral-500 mt-2 line-clamp-2">{c.description}</div>
          </button>
        ))}
      </div>

      {active && (
        <div className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ {active.subject} · {active.grade}</div>
              <h2 className="font-display font-bold text-2xl">{active.title}</h2>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowLesson(true)} className="text-sm font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5"><Plus size={12} className="inline" /> Lesson</button>
              <button onClick={() => { setActive(null); setLessons([]); }} className="text-sm px-2"><X size={14} /></button>
            </div>
          </div>
          {lessons.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6 text-center border border-dashed border-neutral-200">No lessons yet.</div>
          ) : lessons.map((l) => (
            <div key={l.id} className="py-3 border-b border-neutral-100 last:border-0">
              <div className="font-medium text-sm flex items-center gap-2"><BookOpen size={14} /> {l.title}</div>
              {l.content && <div className="text-xs text-neutral-600 mt-1 whitespace-pre-wrap">{l.content.slice(0, 200)}{l.content.length > 200 ? "…" : ""}</div>}
            </div>
          ))}
        </div>
      )}

      {show && <NewCourseModal onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
      {showLesson && active && <NewLessonModal courseId={active.id} onClose={() => setShowLesson(false)} onCreated={async () => { setShowLesson(false); await openCourse(active); }} />}
    </div>
  );
}

function NewCourseModal({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", subject: "", grade: "", description: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/lms/courses", f); toast.success("Course created"); onCreated(); }
    catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  return (
    <Modal title="New course" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <input className={inp} required placeholder="Title *" value={f.title} onChange={set("title")} />
        <div className="grid grid-cols-2 gap-3">
          <input className={inp} placeholder="Subject" value={f.subject} onChange={set("subject")} />
          <input className={inp} placeholder="Grade" value={f.grade} onChange={set("grade")} />
        </div>
        <textarea className={inp} rows={3} placeholder="Description" value={f.description} onChange={set("description")} />
        <button disabled={loading} className="w-full bg-[#0A0C10] text-white font-bold py-3 disabled:opacity-60">{loading ? "Creating…" : "Create course"}</button>
      </form>
    </Modal>
  );
}

function NewLessonModal({ courseId, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", content: "", video_url: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post(`/lms/courses/${courseId}/lessons`, f); toast.success("Lesson added"); onCreated(); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  return (
    <Modal title="New lesson" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <input className={inp} required placeholder="Title *" value={f.title} onChange={set("title")} />
        <input className={inp} placeholder="Video URL (optional)" value={f.video_url} onChange={set("video_url")} />
        <textarea className={inp} rows={6} placeholder="Lesson content / notes (markdown ok)" value={f.content} onChange={set("content")} />
        <button disabled={loading} className="w-full bg-[#0A0C10] text-white font-bold py-3 disabled:opacity-60">{loading ? "Saving…" : "Add lesson"}</button>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-2xl tracking-tight">{title}</h2>
          <button onClick={onClose}><X /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";
